/**
 * Get the absolute Y position of an element
 *
 * @param  {HTMLElement} ele
 */
function getElementY(ele: HTMLElement) {
  return window.scrollY + ele.getBoundingClientRect().top;
}

/**
 * Scroll to a target element with support for offset
 * Default behavior is to scroll smoothly without offset with a duration of 500ms
 *
 * @param  {HTMLElement} element
 * @param  {number} padding
 * @param  {number} duration
 */
function scrollToTarget(
  element: HTMLElement,
  padding: number = 0,
  duration: number = 500,
  smooth: boolean = true,
) {
  // Get current scroll pos
  const currentScrollPos = window.scrollY;

  // Get element position
  const elementY = getElementY(element);

  // Get scroll target position
  const targetY =
    document.body.scrollHeight - elementY < window.innerHeight
      ? document.body.scrollHeight - window.innerHeight
      : elementY;

  // Diff target and current position
  const diff = targetY - padding - currentScrollPos;

  // Declare easing funtion
  const easing = (t) => (t < 0.5 ? 4 * t * t * t : (t - 1) * (2 * t - 2) * (2 * t - 2) + 1);

  // Create start variable
  let start;

  if (!diff) return;

  if (!smooth) {
    window.scrollTo(0, targetY - padding);
    return;
  }

  window.requestAnimationFrame(function step(timestamp) {
    if (!start) start = timestamp;
    // Elapsed miliseconds since start of scrolling.

    const time = timestamp - start;
    // Get percent of completion in range [0, 1].

    let percent = Math.min(time / duration, 1);
    percent = easing(percent);

    window.scrollTo(0, currentScrollPos + diff * percent);

    // Proceed with animation as long as we wanted it to.
    if (time < duration) {
      window.requestAnimationFrame(step);
    }
  });
}

/**
 * Removes the hash from the url
 * Also changes history to remove hashed url from history
 */
function removeHash() {
  // Quickly remove hash content to prevent navigation
  window.location.hash = '';

  // Set current history state to remove hash
  window.history.pushState('', document.title, window.location.pathname + window.location.search);
}

/**
 * Initalize the hash navigation on page load
 *
 * Also adds hash navigation support for all anchor links
 */
function HashNavigator() {
  // Instantly return if no hash set
  if (!window.location.hash) return;

  // Store the hash in variable
  const { hash } = window.location;

  // Remove window hash
  removeHash();

  // Get scroll target
  const scrollTarget = <HTMLElement>document.querySelector(hash);

  // Return if target does not exist
  if (!scrollTarget) return;

  // Animate to the hash location
  scrollToTarget(scrollTarget, 150, 1500);

  // Add event listener to all anchor links with hash
  document.querySelectorAll('a[href^="#"]').forEach((anchor) => {
    anchor.addEventListener('click', (e) => {
      // Prevent default behavior
      e.preventDefault();

      // Get scroll target
      const scrollTargetA = <HTMLElement>document.querySelector(anchor.getAttribute('href'));

      // Return if target does not exist
      if (!scrollTargetA) return;

      // Animate to the hash location
      scrollToTarget(scrollTargetA, 150, 1500);
    });
  });
}

export default HashNavigator;
export { getElementY, scrollToTarget, removeHash };
